import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Trash2,
  Image as ImageIcon,
  Maximize2,
} from 'lucide-react';
import type { GraphicsQuestion, GraphicsAnswerItem, UploadStatus } from '../types/graphics';
import { graphicsStorage } from '../utils/graphicsStorage';
import { supabase } from '../supabase/client';

interface GraphicsAnswerUploadProps {
  question: GraphicsQuestion;
  examId: string;
  questionId: string;
  userId: string;
  disabled?: boolean;
  onAnswerChange?: () => void;
}

interface UploadBoxState {
  image: string | null;
  status: UploadStatus | null;
}

interface ItemUploadState {
  boxes: UploadBoxState[];
}

export default function GraphicsAnswerUpload({
  question,
  examId,
  questionId,
  userId,
  disabled = false,
  onAnswerChange,
}: GraphicsAnswerUploadProps) {
  const [uploadStates, setUploadStates] = useState<Record<string, ItemUploadState>>({});
  const [isDragging, setIsDragging] = useState<Record<string, boolean>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [activeBoxIndex, setActiveBoxIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const isProcessingRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const loadAnswers = async () => {
      const savedAnswers = await graphicsStorage.getAnswers(examId, userId);
      const questionAnswers = savedAnswers[questionId] || {};
      const initialStates: Record<string, ItemUploadState> = {};

      question.answerItems.forEach(item => {
        const savedImages = questionAnswers[item.id] || [];
        const boxes: UploadBoxState[] = [];
        for (let i = 0; i < item.maxCount; i++) {
          boxes.push({
            image: savedImages[i] || null,
            status: null,
          });
        }
        initialStates[item.id] = { boxes };
      });
      setUploadStates(initialStates);
    };
    loadAnswers();
  }, [examId, questionId, userId, question.answerItems]);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const compressImage = async (file: File): Promise<string> => {
    // 压缩图片为 Blob，然后上传到 storage 桶，返回 public URL
    // 避免将 base64 数据直接存入数据库导致行大小超限
    return new Promise((resolve, reject) => {
      if (file.type === 'image/bmp') {
        reject(new Error('BMP格式不支持，请转换为JPG/PNG后上传'));
        return;
      }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('无法创建 canvas 上下文'));
        return;
      }

      img.onload = async () => {
        let { width, height } = img;
        const maxDimension = 2000;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // 转 webp Blob，逐步降低质量直到小于 512KB
        const maxOutputSize = 512 * 1024;
        const tryBlob = (q: number): Promise<Blob> => new Promise((res, rej) => {
          canvas.toBlob(
            (b) => b ? res(b) : rej(new Error('图片压缩失败')),
            'image/webp',
            q
          );
        });

        try {
          let quality = 0.85;
          let blob = await tryBlob(quality);
          while (blob.size > maxOutputSize && quality > 0.3) {
            quality -= 0.1;
            blob = await tryBlob(quality);
          }

          // 上传到 storage 桶
          const fileName = `graphics-answers/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.webp`;
          const { data, error } = await supabase.storage
            .from('paper-images')
            .upload(fileName, blob, { contentType: 'image/webp', upsert: false });

          if (error) throw error;

          const { data: { publicUrl } } = supabase.storage
            .from('paper-images')
            .getPublicUrl(data.path);

          resolve(publicUrl);
        } catch (err) {
          reject(err instanceof Error ? err : new Error('图片上传失败'));
        }
      };

      img.onerror = () => reject(new Error('图片加载失败'));

      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsDataURL(file);
    });
  };

  const setBoxStatus = (itemId: string, boxIndex: number, status: UploadStatus | null) => {
    setUploadStates(prev => {
      const itemState = prev[itemId];
      if (!itemState) return prev;
      const newBoxes = [...itemState.boxes];
      newBoxes[boxIndex] = { ...newBoxes[boxIndex], status };
      return { ...prev, [itemId]: { boxes: newBoxes } };
    });
  };

  const processFile = async (
    file: File,
    itemId: string,
    boxIndex: number,
    item: GraphicsAnswerItem
  ): Promise<string | null> => {
    const statusId = generateId();

    if (!file.type.startsWith('image/')) {
      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'error', progress: 0, message: '格式不支持，请上传图片文件' });
      setTimeout(() => setBoxStatus(itemId, boxIndex, null), 3000);
      return null;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'error', progress: 0, message: '仅支持 JPG、PNG、WebP、GIF 格式' });
      setTimeout(() => setBoxStatus(itemId, boxIndex, null), 3000);
      return null;
    }

    if (file.size > item.maxSizeMB * 1024 * 1024) {
      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'error', progress: 0, message: `图片超过${item.maxSizeMB}MB限制` });
      setTimeout(() => setBoxStatus(itemId, boxIndex, null), 3000);
      return null;
    }

    try {
      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'uploading', progress: 30, message: '正在压缩并上传...' });

      const publicUrl = await compressImage(file);

      if (!graphicsStorage.isStorageAvailable()) {
        setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'error', progress: 0, message: '浏览器存储空间不足，建议清理缓存后继续作答' });
        setTimeout(() => setBoxStatus(itemId, boxIndex, null), 3000);
        return null;
      }

      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'success', progress: 100, message: '上传成功' });
      setTimeout(() => setBoxStatus(itemId, boxIndex, null), 2000);
      return publicUrl;
    } catch (error) {
      const msg = error instanceof Error ? error.message : '图片处理失败，请重试';
      setBoxStatus(itemId, boxIndex, { itemId: statusId, status: 'error', progress: 0, message: msg });
      setTimeout(() => setBoxStatus(itemId, boxIndex, null), 3000);
      return null;
    }
  };

  const handleFileSelect = async (file: File, itemId: string, boxIndex: number, item: GraphicsAnswerItem) => {
    if (disabled || isProcessingRef.current[itemId]) return;
    isProcessingRef.current[itemId] = true;

    const result = await processFile(file, itemId, boxIndex, item);

    if (result) {
      setUploadStates(prev => {
        const itemState = prev[itemId];
        if (!itemState) return prev;
        const newBoxes = [...itemState.boxes];
        newBoxes[boxIndex] = { ...newBoxes[boxIndex], image: result };
        return { ...prev, [itemId]: { boxes: newBoxes } };
      });

      const allImages = (uploadStates[itemId]?.boxes.map(b => b.image).filter(Boolean) as string[]) || [];
      allImages[boxIndex] = result;
      await graphicsStorage.saveAnswer(examId, userId, questionId, itemId, allImages);
      const currentAnswers = await graphicsStorage.getAnswers(examId, userId);
      await graphicsStorage.autoSave(examId, userId, currentAnswers);
      onAnswerChange?.();
    }

    isProcessingRef.current[itemId] = false;
  };

  const handleBoxClick = (itemId: string, boxIndex: number) => {
    if (disabled) return;
    setActiveBoxIndex(boxIndex);
    const input = fileInputRefs.current[itemId];
    if (input) {
      input.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, itemId: string, item: GraphicsAnswerItem) => {
    const file = e.target.files?.[0];
    if (file && activeBoxIndex !== null) {
      handleFileSelect(file, itemId, activeBoxIndex, item);
    }
    e.target.value = '';
    setActiveBoxIndex(null);
  };

  const handleDelete = async (itemId: string, boxIndex: number) => {
    setUploadStates(prev => {
      const itemState = prev[itemId];
      if (!itemState) return prev;
      const newBoxes = [...itemState.boxes];
      newBoxes[boxIndex] = { image: null, status: null };
      return { ...prev, [itemId]: { boxes: newBoxes } };
    });

    const allImages = (uploadStates[itemId]?.boxes.map((b, i) => i === boxIndex ? null : b.image).filter(Boolean) as string[]) || [];
    await graphicsStorage.saveAnswer(examId, userId, questionId, itemId, allImages);
    const currentAnswers = await graphicsStorage.getAnswers(examId, userId);
    await graphicsStorage.autoSave(examId, userId, currentAnswers);
    onAnswerChange?.();
  };

  const handleDragOver = (e: React.DragEvent, itemId: string) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(prev => ({ ...prev, [itemId]: true }));
  };

  const handleDragLeave = (e: React.DragEvent, itemId: string) => {
    e.preventDefault();
    setIsDragging(prev => ({ ...prev, [itemId]: false }));
  };

  const handleDrop = async (e: React.DragEvent, itemId: string, boxIndex: number, item: GraphicsAnswerItem) => {
    e.preventDefault();
    setIsDragging(prev => ({ ...prev, [itemId]: false }));
    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file, itemId, boxIndex, item);
    }
  };

  const handlePaste = useCallback(
    (e: ClipboardEvent, itemId: string, boxIndex: number, item: GraphicsAnswerItem) => {
      if (disabled) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            handleFileSelect(file, itemId, boxIndex, item);
          }
          break;
        }
      }
    },
    [disabled]
  );

  return (
    <div className="space-y-6">
      {question.answerItems.map((item, itemIndex) => {
        const itemState = uploadStates[item.id];
        const isItemDragging = isDragging[item.id] || false;
        const uploadedCount = itemState?.boxes.filter(b => b.image).length || 0;

        return (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {item.name || `作答项${itemIndex + 1}`}
                  </span>
                  {item.required && (
                    <span className="text-xs text-red-500">*</span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {uploadedCount}/{item.maxCount}
                </span>
              </div>
              {item.placeholder && (
                <p className="text-xs text-gray-500 mt-1">{item.placeholder}</p>
              )}
            </div>

            <div className="p-4">
              <div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
                onDragOver={(e) => handleDragOver(e, item.id)}
                onDragLeave={(e) => handleDragLeave(e, item.id)}
              >
                {itemState?.boxes.map((box, boxIndex) => (
                  <div
                    key={boxIndex}
                    data-box-index={boxIndex}
                    onClick={() => !box.image && handleBoxClick(item.id, boxIndex)}
                    onDrop={(e) => handleDrop(e, item.id, boxIndex, item)}
                    onPaste={(e) => handlePaste(e as unknown as ClipboardEvent, item.id, boxIndex, item)}
                    className={`
                      relative aspect-square rounded-lg border-2 border-dashed
                      transition-all duration-200 overflow-hidden
                      ${box.image
                        ? 'border-solid border-blue-500 bg-blue-50'
                        : isItemDragging
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                      }
                      ${!disabled && !box.image ? 'cursor-pointer' : 'cursor-default'}
                    `}
                  >
                    <input
                      ref={el => { fileInputRefs.current[item.id] = el; }}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => handleFileInputChange(e, item.id, item)}
                      disabled={disabled}
                    />

                    {box.image ? (
                      <div className="relative w-full h-full group">
                        <img
                          src={box.image}
                          alt={`上传图片 ${boxIndex + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(box.image);
                            }}
                            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          >
                            <Maximize2 className="w-4 h-4 text-gray-700" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id, boxIndex);
                            }}
                            className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                          {boxIndex + 1}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full p-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mb-2">
                          <Upload className="w-4 h-4 text-gray-500" />
                        </div>
                        <p className="text-xs text-gray-500 text-center">
                          点击上传或拖拽图片
                        </p>
                        <p className="text-xs text-gray-400 text-center mt-1">
                          不超过{item.maxSizeMB}M
                        </p>
                      </div>
                    )}

                    {box.status && (
                      <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                        {box.status.status === 'uploading' && (
                          <>
                            <Loader2 className="w-6 h-6 text-blue-500 animate-spin mb-2" />
                            <p className="text-xs text-gray-600">{box.status.message}</p>
                          </>
                        )}
                        {box.status.status === 'success' && (
                          <>
                            <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
                            <p className="text-xs text-green-600">{box.status.message}</p>
                          </>
                        )}
                        {box.status.status === 'error' && (
                          <>
                            <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
                            <p className="text-xs text-red-600 text-center px-2">{box.status.message}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        );
      })}

      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-10 right-0 p-2 text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={previewImage}
                alt="预览"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                loading="lazy"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
